import { useCallback } from "react";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import { buildGroupFetchPayload } from "../utils/InputRatingUtils";

const useGroupData = ({
    selectedGroups,
    selectedEpr,
    selectedTemplate,
    setExistingStudentComments,
    setGroupOptions,
    setSelectedGroupContent,
    token
}) => {
    const fetchSelectedGroupInfo = useCallback(async () => {
        if (!selectedGroups?.length || !selectedEpr || !selectedTemplate) return;

        const payload = buildGroupFetchPayload({ selectedGroups, selectedEpr, selectedTemplate });
        const options = {
            action: "getGroupsRatings",
            data: payload,
            token
        };

        ElementaryProgressReportDao(options).then((response) => {
            if (response?.data?.payload) {
                const payload = response.data.payload;
                const selectedGroupIdsToMeta = {};

                selectedGroups.forEach((group) => {
                    if (group.groupType === "SHARED" && group.sharedMeta) {
                        selectedGroupIdsToMeta[group.sharedMeta.sharedGroupId] = group.sharedMeta.sharedGradingCriteria;
                    }
                });

                const enrichedGroups = payload.map((group) => {
                    const sharedGradingCriteria = selectedGroupIdsToMeta[group.groupId] || null;
                    return {
                        ...group,
                        sharedGradingCriteria
                    };
                });

                setSelectedGroupContent(enrichedGroups);
            }
        });
    }, [selectedGroups, selectedEpr, selectedTemplate, setSelectedGroupContent, token]);

    const fetchSelectedGroupStudentComments = useCallback(async () => {
        if (!selectedGroups?.length || !selectedEpr || !selectedTemplate) return;

        const payload = buildGroupFetchPayload({ selectedGroups, selectedEpr, selectedTemplate });
        const options = {
            action: "getGroupStudentCriteriaComments",
            data: payload,
            token
        };

        ElementaryProgressReportDao(options).then((response) => {
            if (response?.data?.payload) {
                const payload = response.data.payload;
                setExistingStudentComments(payload);
            }
        });
    }, [selectedGroups, selectedEpr, selectedTemplate, setExistingStudentComments, token]);

    const fetchAllGroupOptions = useCallback(() => {
        if (!selectedEpr || !selectedTemplate || !token) return;

        const gradingCriteria = selectedEpr.gradingCriteriaList.find(
            (criteria) => parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
        );
        if (!gradingCriteria) return;

        const options = {
            action: "getAllGroups",
            gradingProfileId: selectedEpr.key,
            gradingCriteriaId: gradingCriteria.key,
            token
        };

        ElementaryProgressReportDao(options).then((response) => {
            if (response?.data?.payload) {
                const homeroomGroups = response.data.payload.teacherHomeroomGroups;
                const sharedGroups = response.data.payload.sharedGroupInfos;

                const groupOptions = [
                    {
                        label: "Homeroom Groups",
                        options: homeroomGroups
                            .filter((g) => !g.groupName.includes("INTERNAL_"))
                            .map((group) => ({
                                value: `homeroom-${group.groupId}`,
                                label: group.groupName,
                                groupType: "HOMEROOM"
                            }))
                    },
                    {
                        label: "Groups Shared With Me",
                        options: sharedGroups.map((group) => ({
                            value: `shared-${group.sharedGroupId}`,
                            label: `${group.teacherName} - ${group.sharedGroupName} - ${group.sharedPermission === "EDIT" ? "Edit" : "View Only"}`,
                            groupType: "SHARED",
                            sharedMeta: group
                        }))
                    }
                ];

                setGroupOptions(groupOptions);
            }
        });
    }, [selectedEpr, selectedTemplate, token, setGroupOptions]);

    return {
        fetchAllGroupOptions,
        fetchSelectedGroupInfo,
        fetchSelectedGroupStudentComments
    };
};

export default useGroupData;
